import { faShareNodes, faCopy } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Grid,
  Dialog,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  FormLabel,
  Typography,
  Alert,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { Props, State } from "./interfaces";
import { IconButton, GeneratedLinkContainer, StyledDialogContent } from "./styles";

import ConfirmationChangesDialog from "../../../containers/ConfirmationChangesDialog/ConfirmationChangesDialog";
import DialogActions from "../../../containers/DialogActions/DialogActions";
import DialogTitle from "../../../containers/DialogTitle/DialogTitle";
import { DocumentLinkSTO, LegalEntityUserFilter } from "../../../models/document-link";
import { getItemPeriodValue } from "../../../models/documents/period";
import { UserRef } from "../../../models/user";
import AutocompleteDropdown from "../../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import ExpandableItem from "../../../reusable-components/ExpandableItem/ExpandableItem";

import {
  DocumentLinkApiService,
  generateDocumentLink,
} from "../../../services/document-link/document-link-api.service";

import { intlFormatDateISO } from "../../../utils/time-utils";
import { StyledAlertInfo } from "../../../views/shared-document/styles";
import LoadingOverlay from "../../LoadingOverlay/LoadingOverlay";

const ShareDocument = ({ document }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.share_document"; // tRP - translation root path

  const [state, setState] = useState<State>({
    loading: false,
    isShareDialogOpen: false,
    error: "",
    isLinkGenerated: false,
    generatedLink: "",
    validity: 3,
    users: [],
    disableSaveButton: true,
    isConfirmationChangesDialogOpen: false,
  });

  const handleChangeFieldValue = (field: string, value: string | UserRef[]) => {
    setState((state) => ({
      ...state,
      [field]: value,
      disableSaveButton: field === "users" ? false : state.disableSaveButton,
    }));
  };
  // const handleChangeUsers = ( value : UserRef[]) =>{
  //     setState((state)=>({
  //         ...state,
  //         users: value ,
  //         disableSaveButton : false
  //       }));
  // }
  // const handleChangeValidity = (value:number ) => {

  //     setState({
  //         ...state,
  //         validity: parseInt(value)
  //       });
  // };

  const handleGenerateLink = async () => {
    setState({ ...state, loading: true });

    if (state.users.length > 0) {
      try {
        const new_id = uuidv4();
        const documentLink: DocumentLinkSTO = {
          id: new_id,
          isDeleted: false,
          document,
          validityDays: state.validity,
          users: state.users,
        };

        // const response = await new DocumentLinkApiService().saveItems([documentLink])
        // toastifyResponse(response)
        const response = await generateDocumentLink(documentLink);

        if (!response.error) {
          setState({
            ...state,
            generatedLink: `${window.location.origin}/shared-document/${new_id}`,
            isLinkGenerated: true,
            loading: false,
            disableSaveButton: true,
          });
          navigator.clipboard.writeText(`${window.location.origin}/shared-document/${new_id}`);
        } else {
          console.log(!response.error);
        }
      } catch (error) {
        setState({ ...state, error: error as string });
      }
    }
  };
  const handleCloseDialog = () =>
    setState({
      loading: false,
      isShareDialogOpen: false,
      error: "",
      isLinkGenerated: false,
      generatedLink: "",
      validity: 30,
      users: [],
      disableSaveButton: true,
      isConfirmationChangesDialogOpen: false,
    });

  return (
    <>
      <IconButton
        title={t(`${tRP}.share_button`)}
        onClick={() => {
          setState({ ...state, isShareDialogOpen: true });
        }}
      >
        <FontAwesomeIcon icon={faShareNodes} />
      </IconButton>

      {state.isShareDialogOpen && (
        <>
          <Dialog
            open={state.isShareDialogOpen}
            fullWidth
            onClose={() => setState({ ...state, isConfirmationChangesDialogOpen: true })}
          >
            <LoadingOverlay loading={state.loading}>
              <DialogTitle
                title={t(`${tRP}.dialog.title`)}
                handleClose={handleCloseDialog}
              />
              <StyledDialogContent>
                <ExpandableItem
                  expanderContainer={() => (
                    <Typography>
                      {t(`${tRP}.dialog.expandable_item.expander.note`)} : <b>{document.name}</b>{" "}
                    </Typography>
                  )}
                  expandedContainer={
                    <Grid>
                      <Typography>
                        {t(`${tRP}.dialog.expandable_item.expanded.legal_entity`)} : <b>{document.legalEntity?.name}</b>
                      </Typography>
                      <Typography>
                        {t(`${tRP}.dialog.expandable_item.expanded.document_category`)}:{" "}
                        <b>{document.category?.name}</b>
                      </Typography>
                      <Typography>
                        {t(`${tRP}.dialog.expandable_item.expanded.document_type`)} : <b>{document.type?.name}</b>
                      </Typography>
                      {!!document.employee && (
                        <Typography>
                          {t("common_words.employee")} : <b>{document.employee?.name}</b>
                        </Typography>
                      )}
                      {document.type?.dateInsteadPeriod ? (
                        <Typography>
                          {t(`${tRP}.dialog.expandable_item.expanded.document_date`)} :{" "}
                          <b>{`${document?.documentDate ? intlFormatDateISO(document.documentDate) : ""}`}</b>
                        </Typography>
                      ) : (
                        <Typography>
                          {t(`${tRP}.dialog.expandable_item.expanded.document_period`)} :{" "}
                          <b>{`${document?.period ? getItemPeriodValue(document.period) : ""}`}</b>
                        </Typography>
                      )}
                    </Grid>
                  }
                />

                <FormControl>
                  <FormLabel>{t(`${tRP}.dialog.form.validity_label`)}</FormLabel>
                  <RadioGroup
                    defaultValue="3"
                    name="validity"
                    onChange={(event) => handleChangeFieldValue("validity", event.target.value)}
                  >
                    <FormControlLabel
                      value="3"
                      control={<Radio />}
                      label="3 days"
                      disabled={state.isLinkGenerated}
                    />
                    <FormControlLabel
                      value="7"
                      control={<Radio />}
                      label="7 days"
                      disabled={state.isLinkGenerated}
                    />
                    <FormControlLabel
                      value="15"
                      control={<Radio />}
                      label="15 days"
                      disabled={state.isLinkGenerated}
                    />
                  </RadioGroup>
                </FormControl>

                <AutocompleteDropdown
                  required={true}
                  labelKey={t(`${tRP}.dialog.form.share_with_label`)}
                  serviceApi={new DocumentLinkApiService()}
                  apiLoadItemsPath={"/GetLegalEntityUsers"}
                  propsFilter={{ legalEntityId: document?.legalEntity?.id } as LegalEntityUserFilter}
                  propsItem={state}
                  field={"users"}
                  isMultiSelect
                  handleChange={handleChangeFieldValue}
                  disabled={state.isLinkGenerated}
                />

                {!state.isLinkGenerated && (
                  <StyledAlertInfo
                    severity="info"
                    variant="outlined"
                  >
                    {t(`${tRP}.dialog.form.share_info`)}
                  </StyledAlertInfo>
                )}

                {state.isLinkGenerated && (
                  <>
                    <GeneratedLinkContainer>
                      <Typography>{state.generatedLink}</Typography>
                      <IconButton
                        title={t(`${tRP}.dialog.form.copy`)}
                        onClick={() => {
                          navigator.clipboard.writeText(state.generatedLink ?? "");
                        }}
                      >
                        <FontAwesomeIcon icon={faCopy} />
                      </IconButton>
                    </GeneratedLinkContainer>
                    <Alert
                      variant="outlined"
                      severity="info"
                    >
                      {" "}
                      {t(`${tRP}.dialog.form.copied_to_clipboard`)}
                    </Alert>
                  </>
                )}
              </StyledDialogContent>

              <DialogActions
                handleCancel={() => setState({ ...state, isConfirmationChangesDialogOpen: true })}
                handleSave={handleGenerateLink}
                error={state.error}
                disabledSaveButton={state.disableSaveButton}
                disabledCancelButton={state.isLinkGenerated}
                customSaveName={t(`${tRP}.dialog.dialog_actions.generate_link`)}
              />
            </LoadingOverlay>
          </Dialog>
          <ConfirmationChangesDialog
            open={state.isConfirmationChangesDialogOpen}
            handleCancel={() => setState({ ...state, isConfirmationChangesDialogOpen: false })}
            handleDiscard={handleCloseDialog}
          />
        </>
      )}
    </>
  );
};

export default ShareDocument;
